import { Box, Button, IconButton, Popover, useTheme } from "@mui/material";
import { useState } from "react";
import IconConstants from "../../utils/IconConstants";
import { MoreVert } from "@mui/icons-material";
import ConfirmationDlgBox from "../common-components/confirmation-popup";
import ApiClient from "../../apis/Api-client";
import { errorToster, successToster } from "../../shared/toster/Toster";
import { END_POINT } from "../../utils/End-points";
import { useNavigate } from "react-router-dom";
import { Helper } from "../../utils/Helpers";
import { ActionGuard } from "../../core/permissions";
const { getGradientTextStyle } = new Helper();

export const ActionCell = ({ t, data, onEdit, onView, onRefresh }) => {
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const { api } = ApiClient();
  const openpop = Boolean(anchorEl);
  const id = openpop ? "simple-popover" : undefined;
  const [hovered, setHovered] = useState(false);
  const [confirmationDlg, setConfirmationDlg] = useState(false);
  const [confirmationLabel, setConfirmationLabel] = useState("");
  const navigate = useNavigate();

  const fetchEmployeeDetails = async () => {
    try {
      const response = await api.get(
        `${END_POINT.EMPLOYEE.GET_ALL_EMPLOYEES}?employee_key=${encodeURIComponent(
          data.employee_key ?? ""
        )}`
      );
      return response?.status === 200 ? response.data : null;
    } catch (error) {
      if (error?.response?.status === 404) {
        errorToster(t("pages.employee.toast.employee_not_found"));
      } else {
        errorToster(t("pages.employee.toast.employee_fetch_failed"));
      }
      return null;
    }
  };

  const handleEdit = async () => {
    const details = await fetchEmployeeDetails();
    if (!details) return;
    if (typeof onEdit !== "function") {
      errorToster(t("pages.employee.toast.employee_fetch_failed"));
      return;
    }
    onEdit(details);
    setAnchorEl(null);
  };

  const handleView = async () => {
    const details = await fetchEmployeeDetails();
    if (!details) return;
    if (typeof onView !== "function") {
      errorToster(t("pages.employee.toast.employee_fetch_failed"));
      return;
    }
    onView(details);
    setAnchorEl(null);
  };

  const deleteBookingPopup = () => {
    setConfirmationLabel(t("pages.employee.confirmation.delete_employee"));
    setConfirmationDlg(true);
  };

  const handleDeleteEmployee = async () => {
    try {
      const res = await api.delet(
        `${END_POINT.EMPLOYEE.DELETE_EMPLOYEE}/${data.employee_key}`
      );
      setConfirmationDlg(false);
      if (res.status === 200) {
        setAnchorEl(null);
        successToster(
          <span
            dangerouslySetInnerHTML={{
              __html: t("pages.employee.toast.employee_deleted", {
                name: data?.employee_name,
              }),
            }}
          />
        );
        // Call the refresh function to update the table
        if (onRefresh) {
          await onRefresh();
        }
      } else {
        errorToster("Failed to delete employee.");
      }
    } catch (error) {
      errorToster("Something went wrong!");
    }
  };

  const optionsData = [
    {
      label: "View",
      icon: IconConstants.View,
      activeIcon: IconConstants.ViewWhite,
      action: handleView,
      permAction: "view",
    },
    {
      label: "Edit",
      icon: IconConstants.Edit,
      activeIcon: IconConstants.EditWhite,
      action: handleEdit,
      permAction: "update",
    },
    {
      label: "Delete",
      icon: IconConstants.GIDelete,
      activeIcon: IconConstants.DeleteWhite,
      action: deleteBookingPopup,
      permAction: "delete",
    },
  ];

  const handleClosePopover = () => {
    setAnchorEl(null);
    setHovered(null);
  };

  return (
    <>
      <IconButton
        aria-describedby={id}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          background: anchorEl
            ? palette.tab.gradient
            : palette.primary.contrastText,
          color: anchorEl
            ? palette.primary.contrastText
            : palette.text.gradient,
        }}
        size="small"
      >
        <MoreVert
          sx={{
            color: anchorEl ? "#FFFFFF" : palette.icon.gradient,
          }}
          fontSize="small"
        />
      </IconButton>
      <Popover
        id={id}
        open={openpop}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "8px",
          },
        }}
      >
        <Box
          sx={{
            width: { xs: "43.846vw", sm: "25.659vw", md: "13.33vw" },
            padding: "3%",
          }}
        >
          {optionsData.map((val, ind) => (
            <ActionGuard action={val.permAction} key={`${ind}_${val.label}`}>
            <Button
              variant="text"
              fullWidth
              sx={{
                fontSize: { xs: "3.077vw", sm: "14px", md: "1.11vw" },
                fontWeight: 500,
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: 0,
                background:
                  hovered === ind
                    ? palette.button.gradient
                    : palette.primary.contrastText,
                ...(hovered === ind
                  ? { color: palette.text.contrast }
                  : getGradientTextStyle(palette)),
              }}
              onClick={() => {
                setHovered(ind);
                val.action(ind);
              }}
              onMouseEnter={() => setHovered(ind)}
              onMouseLeave={() => setHovered(null)}
            >
              <IconButton>
                <img src={hovered === ind ? val.activeIcon : val.icon} alt="" />
              </IconButton>
              {t(
                `pages.employee.table.action.${val.label
                  .toLowerCase()
                  .replace(/ /g, "_")}`
              )}
            </Button>
            </ActionGuard>
          ))}
        </Box>
      </Popover>
      <ConfirmationDlgBox
        open={confirmationDlg}
        confirmFunction={handleDeleteEmployee}
        setOpen={setConfirmationDlg}
        label={confirmationLabel}
      />
    </>
  );
};
